import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useCustomStyles from "../../hooks/useCustomStyles";
import { selectState } from "../../store/slices/themeSlice";
import { AppDispatch, RootState } from "../../store/store";
import {
  getAdvisorsAction,
  getClientsAction,
  getClientStatus,
  getSubscriberSummary,
} from "../../store/actions/clientsActions";
import { ClientType, PendingClientType } from "../../store/slices/clientsSlice";
import { useNavigate } from "react-router-dom";
import { jsDate, normalizeBusinessName } from "../../utils";
import {
  ADVISOR_COLUMNS,
  PENDING_ADVISOR_COLUMNS,
  withDescriptions,
} from "../../utils/columnDescriptions";
import { accessCodesStyles } from "../../assets/styles/accessCodesStyles";
import HeaderTabs from "../clients/components/HeaderTabs";
import TableComponent from "../clients/components/fff";
import { ContractType } from "../../store/actions/accessCodeActions";
import CustomButton from "../../components/CustomButton";
import AddNewClientModal from "../clients/components/AddNewClientModal";
import ShareAccessCodeClientModal from "../clients/components/ShareAccessCodeClientModal";
import { ErrorTriage } from "../../api/awsApis";

const Advisors = () => {
  const { theme } = useSelector(selectState);
  const classes = useCustomStyles(accessCodesStyles, theme);
  const { user } = useSelector((state: RootState) => state.auth_slice);
  const { subscribers, advisors, pendingAdvisors } = useSelector(
    (state: RootState) => state.clients_slice
  );
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isAddNewClientModalOpen, setIsAddNewClientModalOpen] = useState(false);
  const [isShareAccessCodeModalOpen, setIsShareAccessCodeModalOpen] =
    useState(false);
  const [pendingClient, setPendingClient] = useState<PendingClientType | null>(
    null
  );

  const cancelAddNewClientModal = () => {
    setIsAddNewClientModalOpen(false);
  };
  const confirmAddNewClientModal = (client: PendingClientType) => {
    setPendingClient(client);
    setIsShareAccessCodeModalOpen(true);
    setIsAddNewClientModalOpen(false);
  };
  const cancelShareAccessCodeModal = () => {
    setIsShareAccessCodeModalOpen(false);
  };
  useEffect(() => {
    if (!advisors) {
      if (user?.business?.name) {
        const isAdmin = user.roles.includes("master");
        let businessNames: string[];

        switch (user.business.name) {
          case "sopher":
            businessNames = isAdmin
              ? ["sopher", "SOPHER_ACCOUNTING"]
              : ["sopher"];
            break;

          case "sopher-test": {
            //console.log('sopher-test business name from get clients',user.email, user.user_name ,user.business.name)
            businessNames = isAdmin
              ? ["sopher-test", "SOPHER_ACCOUNTING_TEST"]
              : ["sopher-test"];
            break;
          }

          default: {
            //console.log('default business name from get clients',user.email, user.user_name ,user.business.name)
            businessNames = [user.business.name];
            break;
          }
        }
        dispatch(getClientsAction({ business_names: businessNames, token: user.token }));
        dispatch(getAdvisorsAction({ business_names: businessNames }));
      }
    } else {
      dispatch(
        getSubscriberSummary({
          userIds: advisors?.map((advisor) => advisor.app_user_id),
        })
      );
    }
  }, []);

  const [selectedTab, setSelectedTab] = useState("active"); // Default to pending
  const [filteredAdvisors, setFilteredAdvisors] = useState<ClientType[]>([]);

  let columnsBySelectedTab: {
    key: string;
    label: string;
    sortable: boolean;
  }[] = [];
  if (selectedTab === "active") {
    columnsBySelectedTab = [
      { key: "user_name", label: "Name", sortable: true },
      { key: "email", label: "Email", sortable: true },
      { key: "status", label: "Status", sortable: false },
      { key: "created_at", label: "Start Date", sortable: false },
      { key: "expire_at", label: "Expiry Date", sortable: false },
      { key: "business_name", label: "Business Name", sortable: false },
    ];
  } else {
    columnsBySelectedTab = [
      { key: "pending_user_name", label: "Name", sortable: true },
      { key: "pending_email", label: "Email", sortable: true },
    ];
  }
  // const [selectedTab, setSelectedTab] = useState<string>("active");

  const tabs = [
    { value: "active", label: "Active" },
    { value: "pending", label: "Pending" },
  ];

  useEffect(() => {
    //console.log('advisors use  Effect 1', advisors, subscribers)
    if (advisors && subscribers) {
      //console.log('clients use  Effect 2', clients, subscribers)
      const filtC = advisors.map((client) => {
        const createdAtRaw =
          typeof client.created_at === "string" ||
          client.created_at instanceof Date
            ? new Date(client.created_at)
            : jsDate(client.created_at);
        const createdAtDate =
          createdAtRaw && !isNaN(createdAtRaw.getTime()) ? createdAtRaw : null;
        const expires_date =
          subscribers[client.app_user_id]?.entitlements &&
          Object.values(subscribers[client.app_user_id]?.entitlements)[0]
            ?.expires_date;
        const expireAtDate = expires_date ? new Date(expires_date) : null;
        // console.log('EXPIRES AT DATE:', expireAtDate)

        return {
          ...client,
          verificationStatus: "active" as const, // Ensure this is correctly typed
          status: getClientStatus(client.app_user_id, subscribers),
          business_name: normalizeBusinessName(client.business_name),
          created_at_raw: createdAtDate, // Keep raw date
          created_at:
            createdAtDate && !isNaN(createdAtDate.getTime())
              ? new Intl.DateTimeFormat("en-UK", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                }).format(createdAtDate)
              : "N/A",
          expire_at_raw: expireAtDate, // Keep raw date
          expire_at:
            expireAtDate && !isNaN(expireAtDate.getTime())
              ? new Intl.DateTimeFormat("en-UK", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                }).format(expireAtDate)
              : "N/A",
        };
      });
      setFilteredAdvisors(filtC);
    }
    // console.log('filteredClients', filteredAdvisors, clients);
  }, [advisors, subscribers]);

  const dropdowns = [
    {
      options: [
        { value: "bassam", key: "1" },
        { value: "hamz", key: "2" },
        { value: "ali", key: "3" },
        { value: "mohammed", key: "4" },
      ],
      initialState: "Type",
    },
    {
      options: [
        { value: "Admin", key: "1" },
        { value: "User", key: "2" },
        { value: "Guest", key: "3" },
      ],
      initialState: "Role",
    },
  ];
  const handleAdvisorClick = (
    advisor: ClientType | ContractType | ErrorTriage
  ) => {
    // console.log('handleAdvisorClick ::', advisor.id);
    navigate(`/advisorDetails`, { state: { advisor } });
  };

  return (
    <div className={classes.container}>
      <div className={classes.tableContainer}>
        <h2 className={classes.header}>MANAGE ADVISORS</h2>
        <div className={classes.buttonContainer}>
          <CustomButton
            title={"Add New Advisor"}
            handleClick={() => setIsAddNewClientModalOpen(true)}
            name={"add-advisor"}
            loading={false}
          />
        </div>
      </div>

      <HeaderTabs
        tabs={tabs}
        selectedTab={selectedTab}
        onSelect={setSelectedTab}
      />
      {selectedTab === "pending" && pendingAdvisors ? (
        <TableComponent
          selectedTab={selectedTab}
          dropdowns={dropdowns}
          columns={withDescriptions(columnsBySelectedTab, PENDING_ADVISOR_COLUMNS)}
          data={pendingAdvisors.map((advisor) => ({
            ...advisor,
            advisor, // Ensure this is correctly passed
          }))}
          handleNavigate={handleAdvisorClick}
        />
      ) : selectedTab === "active" ? (
        <TableComponent
          selectedTab={selectedTab}
          dropdowns={dropdowns}
          columns={withDescriptions(columnsBySelectedTab, ADVISOR_COLUMNS)}
          data={filteredAdvisors.map((advisor) => ({
            ...advisor,
            advisor, // Ensure this is correctly passed
          }))}
          handleNavigate={handleAdvisorClick}
        />
      ) : null}

      <AddNewClientModal
        isOpen={isAddNewClientModalOpen}
        onConfirm={confirmAddNewClientModal}
        onCancel={cancelAddNewClientModal}
        role_type="advisor"
      />
      <ShareAccessCodeClientModal
        isOpen={isShareAccessCodeModalOpen}
        client={pendingClient}
        onConfirm={function (): void {
          throw new Error("Function not implemented.");
        }}
        onCancel={cancelShareAccessCodeModal}
      />
    </div>
  );
};

export default Advisors;
